export type DisplayLanguage = 'en' | 'bn';

const BANGLA_DIGITS: Record<string, string> = {
  '0': '০',
  '1': '১',
  '2': '২',
  '3': '৩',
  '4': '৪',
  '5': '৫',
  '6': '৬',
  '7': '৭',
  '8': '৮',
  '9': '৯',
};

/** Converts only visible digits, preserving punctuation, units, and identifiers. */
export function localizeDigits(value: string | number, lang: DisplayLanguage): string {
  const text = String(value);
  return lang === 'bn' ? text.replace(/\d/g, digit => BANGLA_DIGITS[digit]) : text;
}

/** Formats a user-visible count or measurement with locale-appropriate digits. */
export function formatDisplayNumber(
  value: number,
  lang: DisplayLanguage,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(lang === 'bn' ? 'bn-BD' : 'en-BD', options).format(value);
}

/**
 * Formats either a database clock value (HH:mm[:ss]) or a timestamp as 12-hour time.
 * Schedule clock values are interpreted as local wall-clock time; timestamps are shown
 * in Bangladesh time. Bengali output uses Bengali digits while retaining AM/PM.
 */
export function formatTime12(
  value: string | Date | null | undefined,
  lang: DisplayLanguage = 'en',
  fallback = '—',
): string {
  if (!value) return fallback;

  let hours: number;
  let minutes: number;

  if (typeof value === 'string') {
    const clockMatch = value.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/);
    if (clockMatch) {
      hours = Number(clockMatch[1]);
      minutes = Number(clockMatch[2]);
    } else {
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return value;
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Dhaka',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).formatToParts(parsed);
      hours = Number(parts.find(part => part.type === 'hour')?.value);
      minutes = Number(parts.find(part => part.type === 'minute')?.value);
    }
  } else {
    if (Number.isNaN(value.getTime())) return fallback;
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Dhaka',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(value);
    hours = Number(parts.find(part => part.type === 'hour')?.value);
    minutes = Number(parts.find(part => part.type === 'minute')?.value);
  }

  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return typeof value === 'string' ? value : fallback;
  }

  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  const clock = `${hour12}:${String(minutes).padStart(2, '0')}`;

  return `${localizeDigits(clock, lang)} ${period}`;
}
